import type { ReactNode } from "react";

/**
 * Configuration for a single column in the DataGrid.
 * This defines how a column should be rendered and behave.
 */
export interface ColumnConfig<T> {
	/** The key in the data object to access this column's value */
	key: keyof T;
	/** Display label for the column header */
	label: string;
	/** Optional native tooltip on the column header label */
	headerTitle?: string;
	/** Is this column sortable? If so, which backend field to sort by */
	sortable?: boolean;
	sortField?: string;
	/** Custom cell renderer; if omitted, raw value from `key` is rendered */
	cell?: (value: T[keyof T], row: T) => ReactNode;
	/** Column group/category for grouping in visibility modal */
	group?: string;
	/** Default visibility state */
	defaultVisible?: boolean;
	/** Minimum width for the column */
	minWidth?: number;
	/** Ideal/default width for the column */
	width?: number;
	/** When auto-sized, do not assign more than this many pixels (narrow utility columns). */
	maxWidth?: number;
	/** Backend fields required for this column (for optimized API calls) */
	requiredFields?: string[];
	/** Should this column be included in footer totals? */
	summable?: boolean;
	/** Pin column to the left so it stays visible during horizontal scroll. */
	sticky?: boolean;
	/** When set, used instead of `label` for DatagridV2 header (e.g. bulk select-all). */
	header?: (ctx: SimpleColumnBuildContext) => ReactNode;
}

/**
 * Main DataGrid configuration object.
 * Each feature/table should export one of these.
 */
export interface DatagridConfig<T> {
	/** Unique config id (for localStorage keys etc.) */
	id: string;
	/** Column definitions */
	columns: ColumnConfig<T>[];
	/** Default page size */
	defaultPageSize?: number;
	/** Available page size options */
	pageSizeOptions?: number[];
}

/** UI control type for {@link FilterConfig}. */
export enum DatagridFilterInputType {
	Text = "text",
	Select = "select",
	Multiselect = "multiselect",
	Boolean = "boolean",
	Daterange = "daterange",
	Numberrange = "numberrange",
}

/** Backend filter operator sent with {@link BackendFilter}. */
export enum DatagridBackendFilterType {
	Search = "search",
	Exact = "exact",
	Boolean = "boolean",
	Datetimerange = "datetimerange",
	Daterange = "daterange",
	Numberrange = "numberrange",
	Inarray = "inarray",
}

/**
 * A generic backend filter descriptor - basically what you're already sending.
 */
export interface BackendFilter {
	field: string;
	type: DatagridBackendFilterType;
	value: unknown;
}

/**
 * Value type for number range filters.
 */
export interface NumberRangeValue {
	min?: number;
	max?: number;
}

/**
 * Type guard to check if a value is a valid NumberRangeValue with at least one value set.
 */
export function hasNumberRangeValue(value: unknown): value is NumberRangeValue {
	if (!value || typeof value !== "object") return false;
	const range = value as NumberRangeValue;
	return range.min !== undefined || range.max !== undefined;
}

/**
 * Value type for date/datetime range filters.
 */
export interface DateRangeValue {
	start?: string;
	end?: string;
}

/**
 * Push an inarray (multiselect) or exact (single) filter when the value is set.
 */
export function pushInArrayOrExactFilterIfSet(filters: BackendFilter[], field: string, raw: unknown): void {
	if (raw == null) return;
	if (Array.isArray(raw) && raw.length > 0) {
		filters.push({ field, type: DatagridBackendFilterType.Inarray, value: raw });
	} else if (!Array.isArray(raw)) {
		filters.push({ field, type: DatagridBackendFilterType.Exact, value: raw });
	}
}

/**
 * Push number range filters (no conversion) for the given fields.
 */
export function pushNumberRangeFilters(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		if (hasNumberRangeValue(filterValues[field])) {
			filters.push({
				field,
				type: DatagridBackendFilterType.Numberrange,
				value: filterValues[field],
			});
		}
	}
}

/**
 * Push percentage range filters (0–100 input → 0.0–1.0 for API) for the given fields.
 */
export function pushPercentageRangeFilters(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		if (hasNumberRangeValue(filterValues[field])) {
			const range = filterValues[field] as NumberRangeValue;
			filters.push({
				field,
				type: DatagridBackendFilterType.Numberrange,
				value: {
					min: range.min !== undefined ? range.min / 100 : undefined,
					max: range.max !== undefined ? range.max / 100 : undefined,
				},
			});
		}
	}
}

/**
 * Normalize a value that may be string "true"/"false" (from BooleanChoices) to an actual boolean.
 * Backend filter system expects boolean type filters to have actual true/false, not string representations.
 */
function normalizeBooleanFilterValue(value: unknown): boolean {
	if (typeof value === "boolean") {
		return value;
	}
	if (value === "true") {
		return true;
	}
	if (value === "false") {
		return false;
	}
	return Boolean(value);
}

/**
 * Push boolean filters for the given fields when the value is set (not undefined and not null).
 * Converts string "true"/"false" (from BooleanChoices) to actual booleans for backend compatibility.
 */
export function pushBooleanFiltersIfSet(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		const raw = filterValues[field];
		if (raw !== undefined && raw !== null) {
			filters.push({ field, type: DatagridBackendFilterType.Boolean, value: normalizeBooleanFilterValue(raw) });
		}
	}
}

/**
 * Push exact-match filters for the given fields when a non-empty value is set (e.g. enum string filters).
 */
export function pushExactFiltersIfSet(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		const raw = filterValues[field];
		if (raw !== undefined && raw !== null && raw !== "") {
			filters.push({ field, type: DatagridBackendFilterType.Exact, value: raw });
		}
	}
}

/**
 * Push text search filters for the given fields.
 * Only pushes a filter when the value is truthy (e.g. non-empty string).
 */
export function pushSearchFilters(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		const value = filterValues[field];
		if (value) {
			filters.push({ field, type: DatagridBackendFilterType.Search, value });
		}
	}
}

/**
 * Push date/datetime range filters for the given fields.
 */
export function pushDateRangeFilters(filters: BackendFilter[], filterValues: FilterValues, fields: string[]): void {
	for (const field of fields) {
		const dateRange = filterValues[field] as DateRangeValue | undefined;
		if (dateRange?.start || dateRange?.end) {
			filters.push({
				field,
				type: DatagridBackendFilterType.Datetimerange,
				value: { start: dateRange.start, end: dateRange.end },
			});
		}
	}
}

/**
 * How sorting is represented in the feature layer.
 * Direction uses uppercase "ASC" | "DESC" to match createSortArrows.
 */
export interface SortConfig {
	field: string;
	direction: string;
}

/** Context passed when building DatagridV2 headers from `ColumnConfig`. */
export interface SimpleColumnBuildContext {
	pageRowIds: number[];
	sort: SortConfig | undefined;
	handleSort: (newSort?: SortConfig) => void;
}

/**
 * Configuration for fetching dynamic filter options from an API endpoint.
 */
export interface FilterOptionsEndpoint {
	/** The endpoint URL path (e.g., "/admin/operations-management/summary/by-country/filter-options") */
	url: string;
	/** Query parameter name for the filter field (default: "filter") */
	filterParam?: string;
}

/**
 * Filter definition for the FilterSidebar.
 * Describes how a filter should be rendered and its behavior.
 */
export interface FilterConfig<T = unknown> {
	/** Unique identifier for this filter */
	id: string;
	/** Display label */
	label: string;
	/** Filter type determines the UI component used */
	type: DatagridFilterInputType;
	/** Backend field name to filter on */
	field: string;
	/** Backend filter type */
	filterType: DatagridBackendFilterType;
	/** Static options for select/multiselect filters */
	options?: Array<{ value: string | number; label: string }>;
	/** Endpoint configuration to fetch dynamic options for select/multiselect filters */
	optionsEndpoint?: FilterOptionsEndpoint;
	/** Placeholder text */
	placeholder?: string;
	/** Native tooltip on the filter control (e.g. search min-length hint) */
	title?: string;
	/** Default value */
	defaultValue?: T;
	/**
	 * When `type` is `multiselect`, shows a wider dropdown and wrapped labels (e.g. long name/email).
	 * @default false
	 */
	wideMenu?: boolean;
	creatable?: boolean;
	disabled?: boolean;
	/** When `type` is `text`, restricts input to digits (for numeric IDs). */
	numeric?: boolean;
}

/**
 * Filter values state - maps filter id to its current value.
 */
export type FilterValues = Record<string, unknown>;

/**
 * Column visibility state - maps column key to visibility.
 */
export type ColumnVisibility = Record<string, boolean>;

/**
 * Merge stored column order with the current default order.
 * New columns are inserted at their default position instead of appended at the end.
 */
export function mergeColumnOrder(defaultOrder: string[], storedOrder: string[]): string[] {
	const validStored = storedOrder.filter((key) => defaultOrder.includes(key));
	const missing = defaultOrder.filter((key) => !validStored.includes(key));
	const result = [...validStored];

	for (const key of missing) {
		const defaultIndex = defaultOrder.indexOf(key);
		let insertAt = 0;

		for (let i = 0; i < result.length; i++) {
			if (defaultOrder.indexOf(result[i]) < defaultIndex) {
				insertAt = i + 1;
			}
		}

		result.splice(insertAt, 0, key);
	}

	return result;
}

/**
 * A single threshold for color grading.
 * When value >= threshold, the associated color is applied.
 */
export interface ColorGradingThreshold {
	/** The threshold value (percentage as decimal, e.g., 0.75 for 75%) */
	threshold: number;
	/** The background color to apply (hex format) */
	color: string;
}

/**
 * Color grading configuration for a single column.
 */
export interface ColumnColorGrading {
	/** Whether color grading is enabled for this column */
	enabled: boolean;
	/** Thresholds for color grading, sorted ascending by threshold */
	thresholds: ColorGradingThreshold[];
}

/**
 * Color grading state - maps column key to its color grading config.
 */
export type ColorGradingConfig = Record<string, ColumnColorGrading>;

/**
 * Backend adapter configuration.
 * Transforms filter values and sort config into BackendFilter[] format.
 */
export interface BackendAdapter<T> {
	/** Transform filter values into backend filter format */
	buildFilters: (filterValues: FilterValues) => BackendFilter[];
	/** Transform sort config into backend format (if needed) */
	buildSort?: (sort: SortConfig | undefined) => { field: string; direction: string } | undefined;
}

/**
 * Complete table configuration combining columns, filters, and backend adapter.
 */
export interface TableConfig<T> extends DatagridConfig<T> {
	/** Filter definitions for the FilterSidebar */
	filters: FilterConfig[];
	/** Backend adapter for transforming filters/sort */
	backendAdapter: BackendAdapter<T>;
}
